import React, { useState, useEffect } from "react";
import axios from "axios";

const ManageEvents = () => {
    const [events, setEvents] = useState([]);
    const [paginationTokens, setPaginationTokens] = useState({ next: null, prev: [] });
    const [eventToEdit, setEventToEdit] = useState(null);
    const [newEvent, setNewEvent] = useState({
        summary: "",
        description: "",
        startDateTime: "",
        endDateTime: "",
    });

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async (pageToken = null) => {
        try {
            const response = await axios.get("/api/calendar/admin/events", {
                params: { pageToken, maxResults: 5 },
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            setEvents(response.data.items || []);
            setPaginationTokens((prevTokens) => ({
                next: response.data.nextPageToken || null,
                prev: pageToken ? [...prevTokens.prev, pageToken] : prevTokens.prev,
            }));
        } catch (error) {
            console.error("Error fetching events:", error);
        }
    };

    const handleAddEvent = async () => {
        try {
            const payload = eventToEdit || newEvent;
            const url = eventToEdit
                ? `/api/calendar/admin/events/${eventToEdit.id}`
                : "/api/calendar/admin/events";
            const method = eventToEdit ? "put" : "post";

            const response = await axios[method](url, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });

            if (eventToEdit) {
                setEvents((prev) =>
                    prev.map((event) => (event.id === response.data.id ? response.data : event))
                );
            } else {
                setEvents([response.data, ...events]);
            }

            resetForm();
        } catch (error) {
            console.error("Error saving event:", error);
        }
    };

    const handleEditEvent = (event) => {
        const formatToDateTimeLocal = (date) =>
            new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

        setEventToEdit(event);
        setNewEvent({
            summary: event.summary,
            description: event.description || "",
            startDateTime: formatToDateTimeLocal(new Date(event.start.dateTime)),
            endDateTime: formatToDateTimeLocal(new Date(event.end.dateTime)),
        });
    };


    const handleDelete = async (id) => {
        try {
            await axios.delete(`/api/calendar/admin/events/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            setEvents((prev) => prev.filter((event) => event.id !== id));
        } catch (error) {
            console.error("Error deleting event:", error);
        }
    };

    const resetForm = () => {
        setNewEvent({ summary: "", description: "", startDateTime: "", endDateTime: "" });
        setEventToEdit(null);
    };

    const handleDateChange = (field, value) => {
        const updatedEvent = { ...newEvent, [field]: value };

        if (field === "startDateTime" && !eventToEdit) {
            const startTime = new Date(value);
            const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour

            // Format to 'yyyy-MM-ddTHH:mm' for datetime-local
            const formatToDateTimeLocal = (date) =>
                new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

            updatedEvent.endDateTime = formatToDateTimeLocal(endTime);

            if (startTime.getDate() !== endTime.getDate()) {
                alert("The event spans multiple days. Are you sure?");
            }
        }

        setNewEvent(updatedEvent);
    };

    const handlePrevious = () => {
        if (paginationTokens.prev.length > 0) {
            const prevToken = paginationTokens.prev[paginationTokens.prev.length - 2];
            setPaginationTokens((prev) => ({
                next: prev.next,
                prev: prev.prev.slice(0, -1),
            }));
            fetchEvents(prevToken);
        }
    };

    return (
        <div>
            <h2 className="text-2xl font-bold mb-4">Manage Events</h2>
            <div className="mb-4">
                <input
                    type="text"
                    value={newEvent.summary}
                    onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })}
                    placeholder="Event title"
                    className="border px-4 py-2 rounded w-full mb-2"
                />
                <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Event description"
                    className="border px-4 py-2 rounded w-full mb-2"
                ></textarea>
                <label className="block font-semibold">Start Time/Date</label>
                <input
                    type="datetime-local"
                    value={newEvent.startDateTime}
                    onChange={(e) => handleDateChange("startDateTime", e.target.value)}
                    className="border px-4 py-2 rounded w-full mb-2"
                />
                <label className="block font-semibold">End Time/Date</label>
                <input
                    type="datetime-local"
                    value={newEvent.endDateTime}
                    onChange={(e) => setNewEvent({ ...newEvent, endDateTime: e.target.value })}
                    className="border px-4 py-2 rounded w-full mb-2"
                />
                <button
                    onClick={handleAddEvent}
                    className="bg-blue-500 text-white px-4 py-2 mt-2 rounded"
                >
                    {eventToEdit ? "Update Event" : "Add Event"}
                </button>
                {eventToEdit && (
                    <button
                        onClick={resetForm}
                        className="bg-gray-500 text-white px-4 py-2 mt-2 rounded ml-2"
                    >
                        Cancel
                    </button>
                )}
            </div>
            <ul className="space-y-2">
                {events.map((event) => (
                    <li key={event.id} className="border p-2 rounded">
                        <h3 className="font-bold">{event.summary}</h3>
                        <p>{event.description}</p>
                        <p className="text-gray-700">
                            Start: {new Date(event.start.dateTime).toLocaleString()}
                            <br/>
                            End: {new Date(event.end.dateTime).toLocaleString()}
                        </p>

                        <button
                            onClick={() => handleEditEvent(event)}
                            className="text-blue-500 mt-2 block"
                        >
                            Edit
                        </button>
                        <button
                            onClick={() => handleDelete(event.id)}
                            className="text-red-500 mt-2 block"
                        >
                            Delete
                        </button>
                    </li>
                ))}
            </ul>
            <div className="mt-4 flex justify-between">
                {paginationTokens.prev.length > 1 && (
                    <button
                        onClick={handlePrevious}
                        className="bg-gray-500 text-white px-4 py-2 rounded"
                    >
                        Previous
                    </button>
                )}
                {paginationTokens.next && (
                    <button
                        onClick={() => fetchEvents(paginationTokens.next)}
                        className="bg-gray-500 text-white px-4 py-2 rounded"
                    >
                        Next
                    </button>
                )}
            </div>
        </div>
    );
};

export default ManageEvents;
